import { useState } from "react";
import { CheckCircle2, HandCoins, PackageOpen, Store } from "lucide-react";
import { toast } from "sonner";
import { sendChurchRequest, apiError } from "@/lib/api";
import Seo from "@/components/Seo";

const CARGOS = ["Pastor", "Líder", "Responsable", "Librería", "Otro"];
const EMPTY = { nombre: "", cargo: "", iglesia: "", direccion: "", pais: "", telefono: "", email: "", mensaje: "", website: "" };
const inputCls = "w-full rounded-lg border border-[#0f2544]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1f44e0] transition-colors";
const labelCls = "block text-sm font-medium text-[#0f2544] mb-1.5";

const POINTS = [
  { icon: PackageOpen, t: "Libros en depósito", d: "Os enviamos varios títulos de nuestro catálogo. No tenéis que pagar nada por adelantado." },
  { icon: HandCoins, t: "Importante descuento", d: "Recibís los libros con un descuento importante sobre el precio de venta." },
  { icon: Store, t: "Un margen para vuestro ministerio", d: "Los vendéis en vuestra librería o espacio de venta, y el margen ayuda a sostener vuestro ministerio." },
];

function Intro() {
  return (
    <div>
      <p className="hv-overline">Para iglesias y ministerios</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0f2544] mt-3 leading-[1.02]">Libros en tu Iglesia</h1>
      <p className="text-base md:text-lg text-[#4a4a45] mt-6 leading-relaxed">
        Con el fin de acercar la literatura a los creyentes de Europa con materiales edificantes y de calidad, y en nuestro compromiso de ser de bendición al Cuerpo de Cristo, <strong className="text-[#0f2544]">Editorial Harvest</strong> se compromete a enviar a iglesias o ministerios cristianos varios de los títulos que tenemos, seleccionados por ustedes. Los libros se enviarán en la modalidad de «depósito», es decir, sin que tengan que pagar nada por adelantado. Y se irán liquidando conforme se vayan vendiendo.
      </p>
      <p className="text-base md:text-lg text-[#4a4a45] mt-4 leading-relaxed">
        En este sentido, se les aplicará un importante descuento para que puedan venderlos en sus librerías o espacios de venta con un buen margen que les ayude en el sostenimiento del ministerio.
      </p>
      <div className="mt-10 space-y-6">
        {POINTS.map(({ icon: Icon, t, d }) => (
          <div key={t} className="flex gap-4">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#0f2544] text-[#e6c364] shrink-0"><Icon size={19} /></span>
            <div><p className="font-semibold text-[#0f2544]">{t}</p><p className="text-sm text-[#4a4a45] mt-1">{d}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => <label className="block"><span className={labelCls}>{label}</span>{children}</label>;

export default function LibrosIglesia() {
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) return toast.error("Debes aceptar la Política de Privacidad.");
    setLoading(true);
    try { await sendChurchRequest(form); setSent(true); }
    catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Seo title="Libros en tu Iglesia" description="Editorial Harvest envía libros en depósito y con descuento a iglesias y ministerios de Europa, sin pago por adelantado." />
      <section className="max-w-[1300px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20" data-testid="libros-iglesia-page">
        <Intro />
        <div className="rounded-2xl bg-[#eceae4] border border-[#0f2544]/10 p-6 sm:p-8 self-start">
          {sent ? (
            <div className="text-center py-14" data-testid="iglesia-confirmation">
              <CheckCircle2 size={48} className="mx-auto text-[#1f44e0]" />
              <h2 className="font-serif text-2xl text-[#0f2544] mt-5">Solicitud enviada</h2>
              <p className="text-[#4a4a45] mt-3">Gracias. Nos pondremos en contacto con vosotros para ver juntos los libros que deseáis, las formas de envío y todos los detalles.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="iglesia-form">
              <h2 className="font-serif text-2xl font-semibold text-[#0f2544]">Solicita libros para tu iglesia</h2>
              <p className="text-sm text-[#4a4a45]">Una vez recibido el formulario, nos pondremos en contacto contigo para ver juntos los libros que deseáis, las formas de envío, etc.</p>
              <Field label="Nombre y apellidos *"><input required value={form.nombre} onChange={set("nombre")} className={inputCls} data-testid="ig-nombre" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Cargo *">
                  <select required value={form.cargo} onChange={set("cargo")} className={inputCls} data-testid="ig-cargo">
                    <option value="">Selecciona...</option>
                    {CARGOS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Iglesia o ministerio"><input value={form.iglesia} onChange={set("iglesia")} className={inputCls} data-testid="ig-iglesia" /></Field>
              </div>
              <Field label="Dirección *"><input required value={form.direccion} onChange={set("direccion")} className={inputCls} placeholder="Calle, número, ciudad y código postal" data-testid="ig-direccion" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="País *"><input required value={form.pais} onChange={set("pais")} className={inputCls} data-testid="ig-pais" /></Field>
                <Field label="Teléfono de contacto *"><input required type="tel" minLength={6} value={form.telefono} onChange={set("telefono")} className={inputCls} data-testid="ig-telefono" /></Field>
              </div>
              <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="ig-email" /></Field>
              <Field label="Comentarios (opcional)"><textarea rows={3} value={form.mensaje} onChange={set("mensaje")} className={inputCls} data-testid="ig-mensaje" /></Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <label className="flex items-start gap-2 text-sm text-[#4a4a45]">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="accent-[#1f44e0] mt-0.5" data-testid="ig-privacy" />
                <span>He leído y acepto la <a href="/politica-de-privacidad" className="underline">Política de Privacidad</a>.</span>
              </label>
              <button type="submit" disabled={loading} className="w-full rounded-full bg-[#0f2544] px-8 py-4 text-xs font-semibold tracking-[0.12em] uppercase text-white hover:bg-[#1f44e0] transition-colors disabled:opacity-60" data-testid="ig-submit">
                {loading ? "Enviando..." : "Enviar solicitud"}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
